/**
 * Traducciones ES -> EN de las pantallas de detalle de un vault: DetalleVault.tsx (resumen, banda,
 * barra de acciones y sus modales, avisos, proyección), Auditar.tsx y Compartir.tsx.
 *
 * La clave es la cadena en español EXACTA que envuelve `t(...)` en esos componentes; los `{x}` se
 * interpolan tras traducir. NO se traducen nombres propios (Keellabs, Uniswap), símbolos de token
 * ni las variables `{...}`.
 */
export const DETALLE: Record<string, string> = {
  // --- estampa del NFT de la posición ---
  "Tu posición, en cadena": "Your position, on-chain",
  "Tu posición como NFT": "Your position as an NFT",
  "La dibuja el propio contrato del DEX, con el par, la comisión y los bordes del rango dentro del SVG. Es la prueba de que la posición #{tokenId} existe y pertenece a tu vault.":
    "It's drawn by the DEX's own contract, with the pair, fee and range bounds inside the SVG. It's the proof that position #{tokenId} exists and belongs to your vault.",
  // --- pestañas ---
  "Resumen": "Summary",
  "Historial": "History",
  "Ajustes": "Settings",

  // --- cabecera / estado del vault ---
  "← Volver": "← Back",
  "Cargando el vault…": "Loading the vault…",
  "No pude leer este vault.": "I couldn't read this vault.",
  "Reintentar": "Retry",
  "en pausa": "paused",
  "sin posición": "no position",
  "en rango": "in range",
  "fuera de rango": "out of range",
  "Tu posición #{tokenId} en {sitio}": "Your position #{tokenId} on {sitio}",
  "Mi posición en {sitio} ↗": "My position on {sitio} ↗",
  "Pool {par} de {dex} en {sitio}": "{par} pool on {dex} at {sitio}",
  "Ver la pool en {sitio} ↗": "View the pool on {sitio} ↗",
  "Este vault no es tuyo: lo puedes mirar, pero las acciones las firma solo su dueño.":
    "This vault isn't yours: you can view it, but only its owner can sign actions.",

  // --- panel de precio / banda ---
  "Precio ahora": "Price now",
  "{unidad} por {activo}": "{unidad} per {activo}",
  "lo que hay dentro de la posición ahora mismo": "what's inside the position right now",
  "ahora:": "now:",
  "cobrando": "earning",
  "Generar una tarjeta con tu resultado": "Generate a card with your result",
  "Compartir": "Share",

  // --- métricas ---
  "Comisiones generadas": "Fees generated",
  "Compartir este resultado": "Share this result",
  "leyendo el historial…": "reading the history…",
  "todo sigue dentro de Uniswap: aún no se ha cobrado ninguna vez":
    "it's all still inside Uniswap: it hasn't been collected yet",
  "en {cobros} {cobroPalabra} · antes de la comisión de Keellabs ({comision})":
    "over {cobros} {cobroPalabra} · before the Keellabs fee ({comision})",
  "cobro": "collection",
  "cobros": "collections",
  "Tuyo, listo para retirar": "Yours, ready to withdraw",
  "todavía nada: se llena según la posición cobra comisiones":
    "nothing yet: it fills up as the position collects fees",
  "con «Cobrar ganancias» · ya sin los {comision} de comisión":
    "with «Collect earnings» · already minus the {comision} fee",
  "con «Cobrar ganancias», sin tocar tu capital":
    "with «Collect earnings», without touching your capital",
  "Ya retirado a tu wallet": "Already withdrawn to your wallet",
  "salió de este vault y ya está en tu cuenta": "it left this vault and is now in your account",
  "Bolsa de gas": "Gas budget",
  "vacía: el robot no adelanta gas, así que deja de reajustar":
    "empty: the robot doesn't front gas, so it stops rebalancing",
  "la gasta el robot al reajustar · lo que no gaste vuelve entero":
    "the robot spends it when rebalancing · whatever it doesn't spend comes back in full",
  "Reajustes que quedan": "Rebalances remaining",
  "a cero: el robot no puede mover el rango hasta que recargues":
    "at zero: the robot can't move the range until you top it up",
  "cada uno mueve el rango cuando el precio se sale":
    "each one moves the range when the price leaves it",
  "Reserva para comprar barato": "Reserve to buy cheap",
  "la reinyección está apagada: este dinero no hace nada":
    "reinjection is off: this money isn't doing anything",
  "el robot la suelta según cae el precio": "the robot releases it as the price falls",

  // --- resultado hero ---
  "Vas ganando": "You're up",
  "Estás por debajo": "You're down",
  "sobre lo que pusiste": "over what you put in",
  "por debajo · el rango lo recupera si el precio vuelve":
    "below · the range recovers it if the price comes back",
  "Tu dinero ahora ": "Your money now ",
  " · pusiste ": " · you put in ",

  // --- rendimiento ---
  "{pct}% de tu capital": "{pct}% of your capital",
  " · ≈{anual}% anual a este ritmo": " · ≈{anual}% annual at this pace",

  // --- proyección ---
  "Proyección al volumen de ahora": "Projection at current volume",
  "≈ {est}% anual": "≈ {est}% annual",
  "Leyendo el volumen de la pool…": "Reading the pool's volume…",
  "al día": "per day",
  "al mes": "per month",
  "al año": "per year",
  "Sobre los ": "On the ",
  " que tienes desplegados, al ritmo de comisiones que la pool reparte ":
    " you have deployed, at the rate of fees the pool is paying out ",
  "ahora mismo": "right now",
  " y si el precio se queda en el rango. No es lo que ya has ganado —eso es «Comisiones generadas»—: es una estimación que sube y baja con el volumen y el TVL de la pool.":
    " and if the price stays in range. It's not what you've already earned —that's «Fees generated»—: it's an estimate that rises and falls with the pool's volume and TVL.",
  "El indexer aún no tiene una ventana de volumen de esta pool para estimar el ritmo.":
    "The indexer doesn't yet have a volume window for this pool to estimate the pace.",

  // --- tiempo ---
  "Lleva trabajando": "Working for",
  "no encuentro la apertura en el historial de esta red":
    "I can't find the opening in this network's history",

  // --- abrir posición ---
  "Poner el dinero a trabajar": "Put the money to work",
  "Este vault tiene ": "This vault has ",
  " dentro y ninguna posición abierta, así que no está cobrando comisiones. Al abrir se vende lo justo para entrar en el rango y se mintea la posición en Uniswap.":
    " inside and no open position, so it isn't collecting fees. On opening, just enough is sold to enter the range and the position is minted on Uniswap.",
  "Ancho del rango (±%)": "Range width (±%)",
  "Bolsa de gas ({simbolo})": "Gas budget ({simbolo})",
  "El deslizamiento se calcula solo, mirando la profundidad de esta pool: en las finas el propio swap mueve el precio y con el valor de fábrica la apertura revierte.":
    "Slippage is computed automatically, from this pool's depth: in thin ones the swap itself moves the price and with the default value opening reverts.",
  "Este vault tiene la cuota de reajustes a ": "This vault has its rebalance quota at ",
  ": aunque abras, el robot no lo va a reajustar nunca. Recárgala en Ajustes → 🎫 Cuota de reajustes.":
    ": even if you open, the robot will never rebalance it. Top it up in Settings → 🎫 Rebalance quota.",
  "Abrir posición": "Open position",

  // --- barra de acciones: saldo común ---
  "Tienes en tu wallet": "You have in your wallet",
  "no pude leerlo": "I couldn't read it",
  "leyendo…": "reading…",
  "MÁX": "MAX",
  "Firmas": "Signatures",
  "Sale de tu wallet": "Leaves your wallet",
  "Objetivo después": "Target afterwards",

  // --- modal: volver al rango estrecho ---
  "Volver al rango estrecho": "Back to the tight range",
  "Tu rango se ensanchó al recuperar (≈{pct}% de ancho). Esto lo re-centra a tu ":
    "Your range widened while recovering (≈{pct}% wide). This re-centers it to your ",
  "ancho base (≈{pct}%)": "base width (≈{pct}%)",
  " al precio de ahora, ": " at the current price, ",
  "sin vender tu {activo}": "without selling your {activo}",
  ": aportas {ref} para equilibrar y así no cristalizas la pérdida.":
    ": you add {ref} to balance it out so you don't crystallize the loss.",
  "Cuánto {ref} aportas": "How much {ref} you add",
  "Rango ahora": "Range now",
  "≈ {pct}% de ancho": "≈ {pct}% wide",
  "Rango después": "Range afterwards",
  "≈ {pct}%  ·  tu ancho base": "≈ {pct}%  ·  your base width",
  "Aportas de tu wallet": "You add from your wallet",
  "Tu {activo}": "Your {activo}",
  "no se vende — se mantiene": "isn't sold — it's kept",
  "1 o 2 (aprobar · re-centrar)": "1 or 2 (approve · re-center)",
  "Cuanto más {ref} aportes, más apretado queda. Si aportas poco, parte de tu {activo} se queda sin desplegar hasta el siguiente barrido — no se pierde.":
    "The more {ref} you add, the tighter it gets. If you add little, part of your {activo} stays undeployed until the next sweep — it isn't lost.",

  // --- modal: añadir fondos ---
  "Añadir fondos": "Add funds",
  "Este dinero entra en la posición que ": "This money goes into the position you ",
  "ya tienes": "already have",
  ", no crea otra. El objetivo a recuperar sube con él.":
    ", it doesn't create another. The target to recover rises with it.",
  "Cuánto añades": "How much you add",
  "1 o 2 (aprobar · depositar)": "1 or 2 (approve · deposit)",

  // --- modal: bolsa de gas ---
  "El robot adelanta el gas de red de cada reajuste y se reembolsa de esta bolsa. ":
    "The robot fronts the network gas for each rebalance and reimburses itself from this budget. ",
  "No es capital": "It's not capital",
  ": no entra en la posición ni cuenta para el objetivo.":
    ": it doesn't go into the position nor count toward the target.",
  "Cuánto cargas": "How much you load",
  "Bolsa ahora": "Budget now",
  "Bolsa después": "Budget afterwards",
  "La bolsa está a ": "The budget is at ",
  "cero": "zero",
  ": el robot no reajusta hasta que la cargues.":
    ": the robot won't rebalance until you load it.",
  "Cargar gas": "Load gas",
  "Devolverme los {monto} sin gastar": "Return the unspent {monto} to me",

  // --- modal: reserva ---
  "Reserva para comprar más barato": "Reserve to buy cheaper",
  "La reserva se queda retenida y el robot la va soltando ":
    "The reserve stays held and the robot releases it ",
  "según cae el precio": "as the price falls",
  ". Es capital tuyo: el objetivo a recuperar sube con ella.":
    ". It's your capital: the target to recover rises with it.",
  "Cuánto apartas": "How much you set aside",
  "Reserva ahora": "Reserve now",
  "Reserva después": "Reserve afterwards",
  "Tamaño de cada tramo": "Size of each tranche",
  "no cambia — tendrás más inyecciones, no más grandes":
    "doesn't change — you'll have more injections, not bigger ones",
  "La reinyección está ": "Reinjection is ",
  "apagada": "off",
  ": la reserva se quedará quieta hasta que la actives en Ajustes.":
    ": the reserve will sit idle until you enable it in Settings.",
  "Apartar a la reserva": "Set aside to the reserve",
  "Apartar reserva": "Set aside reserve",

  // --- modal: tus ganancias ---
  "Todavía no hay nada que cobrar": "There's nothing to collect yet",
  "Tus ganancias": "Your earnings",
  "Cobra al NFT de Uniswap, te lo manda a la wallet y después ":
    "It collects from the Uniswap NFT, sends it to your wallet and then ",
  "vende los {activo} por {ref}": "sells the {activo} for {ref}",
  ", para que la ganancia te quede entera en una sola moneda. ":
    ", so your earnings end up whole in a single currency. ",
  "Tu capital y tu posición no se tocan.": "Your capital and your position aren't touched.",
  "Cobra al NFT de Uniswap y decide qué hacer con ello. ":
    "It collects from the Uniswap NFT and lets you decide what to do with it. ",
  "Tu capital y tu posición no se tocan": "Your capital and your position aren't touched",
  " en ninguna de las dos opciones.": " in either option.",
  "Convertirla toda a {ref} al cobrar": "Convert it all to {ref} when collecting",
  "La venta se hace ": "The sale happens ",
  "después": "after",
  " de que la ganancia esté en tu wallet, nunca dentro del cobro: si el precio se mueve y no sale, ya la tienes — solo te quedas sin convertir.":
    " the earnings are in your wallet, never inside the collection: if the price moves and it doesn't go through, you already have it — you just don't get the conversion.",
  "Ya apartado": "Already set aside",
  "{monto} {ref}  ·  su comisión ya se cobró": "{monto} {ref}  ·  its fee was already charged",
  "Aún dentro del NFT": "Still inside the NFT",
  "{monto} {ref}  ·  en bruto": "{monto} {ref}  ·  gross",
  "Comisión de Keellabs ({pct}%)": "Keellabs fee ({pct}%)",
  "− {monto} {ref}  ·  solo sobre lo del NFT": "− {monto} {ref}  ·  only on the NFT portion",
  "QUEDA PARA TI": "LEFT FOR YOU",
  "Te llega en": "You receive it in",
  "{ref} y un resto de {activo}  ·  demasiado pequeño para venderlo":
    "{ref} and a bit of {activo}  ·  too small to sell",
  "{ref}  ·  no hay nada en {activo}": "{ref}  ·  there's nothing in {activo}",
  "Se vende después": "Sold afterwards",
  "Deslizamiento máximo": "Max slippage",
  "{pct}%  ·  el mismo que tiene puesto el vault": "{pct}%  ·  the same one the vault has set",
  "2 · cobrar y vender": "2 · collect and sell",
  "En qué te llega": "How you receive it",
  "en {t0} y {t1}": "in {t0} and {t1}",
  "Retirar a mi wallet": "Withdraw to my wallet",
  "Reinvertir": "Reinvest",
  "Cobrar ganancias": "Collect earnings",

  // --- modal: quitar una parte ---
  "Quitar una parte": "Remove a portion",
  "Saca ese porcentaje de la posición y te lo manda a la wallet, en los dos tokens del par. ":
    "It takes that percentage out of the position and sends it to your wallet, in both tokens of the pair. ",
  "El resto sigue trabajando.": "The rest keeps working.",
  "Cuánto sacas": "How much you take out",
  "El objetivo a recuperar baja en la misma proporción.":
    "The target to recover drops by the same proportion.",
  "A tu wallet": "To your wallet",
  "≈ {monto} {ref}  ·  en {t0} y {t1}": "≈ {monto} {ref}  ·  in {t0} and {t1}",
  "Se queda trabajando": "Stays working",
  "Comisiones del tramo": "Fees of this tranche",
  "se cobran y se apartan como ganancia": "are collected and set aside as earnings",
  "Al 100% la posición se cierra entera, pero ": "At 100% the position closes entirely, but ",
  "el gas y la reserva se quedan dentro": "the gas and the reserve stay inside",
  ". Si quieres salir del todo, usa «Sacar todo».":
    ". If you want to exit completely, use «Withdraw everything».",
  "Quitar esa parte": "Remove that portion",

  // --- modal: sacar todo y cerrar ---
  "Sacar todo y cerrar": "Withdraw everything and close",
  "Cierra la posición, cobra lo que Uniswap todavía retiene y ":
    "It closes the position, collects what Uniswap still holds and ",
  "te lo deja todo en {ref}": "leaves it all in {ref} for you",
  ": primero sale a tu wallet en los dos tokens y después se venden los {activo} por {ref}. Son ":
    ": first it goes to your wallet in both tokens and then the {activo} is sold for {ref}. That's ",
  "dos firmas": "two signatures",
  ".": ".",
  "Cierra la posición, cobra lo que Uniswap todavía retiene y te manda ":
    "It closes the position, collects what Uniswap still holds and sends ",
  "todo": "everything",
  " a la wallet, en los dos tokens del par. El vault se queda vacío y archivado, y ":
    " to your wallet, in both tokens of the pair. The vault is left empty and archived, and ",
  "puedes volver a abrirlo": "you can open it again",
  " en {t0}/{t1} cuando quieras — conserva su cuota y sus ajustes.":
    " in {t0}/{t1} whenever you want — it keeps its quota and settings.",
  "Convertirlo todo a {ref} al salir": "Convert it all to {ref} on exit",
  " de que el dinero esté en tu wallet, nunca dentro del cierre: si el precio se mueve y no sale, ya lo tienes todo — solo te quedas sin convertir.":
    " the money is in your wallet, never inside the close: if the price moves and it doesn't go through, you already have it all — you just don't get the conversion.",
  "Empezaste con": "You started with",
  "{monto} {ref}  ·  el capital que aportaste": "{monto} {ref}  ·  the capital you put in",
  "Tu posición ahora": "Your position now",
  "Ganancias, ya sin comisión": "Earnings, already minus the fee",
  "Reserva sin usar": "Unused reserve",
  "CIERRAS CON": "YOU CLOSE WITH",
  "Frente a lo que pusiste": "Against what you put in",
  "Gas sin gastar": "Unspent gas",
  "+ {monto} {ref}  ·  nunca fue capital, vuelve entero":
    "+ {monto} {ref}  ·  it was never capital, it comes back in full",
  "LLEGA A TU WALLET": "REACHES YOUR WALLET",
  "1 si tu vault sabe convertir al salir  ·  si no, 2 (cerrar y vender)":
    "1 if your vault can convert on exit  ·  if not, 2 (close and sell)",
  "Esto ": "This ",
  "no tiene deshacer": "can't be undone",
  ": la posición se cierra y hay que volver a abrirla desde cero, con su gas y su rango.":
    ": the position closes and you have to open it again from scratch, with its gas and range.",
  "Sacar todo": "Withdraw everything",

  // --- avisos de dinero ---
  "El robot no está activado: le quedan 0 reajustes.":
    "The robot isn't active: it has 0 rebalances left.",
  "La cuota es un contador aparte de la bolsa de gas: el gas ":
    "The quota is a counter separate from the gas budget: the gas ",
  "paga": "pays for",
  " cada reajuste, y la cuota es ": " each rebalance, and the quota is ",
  "cuántos permites": "how many you allow",
  ". Con la cuota a cero el robot no toca tu rango aunque la bolsa esté llena.":
    ". With the quota at zero the robot doesn't touch your range even if the budget is full.",
  "Activando…": "Activating…",
  "Activar el robot · 500 reajustes": "Activate the robot · 500 rebalances",
  "La bolsa de gas está vacía.": "The gas budget is empty.",
  "El robot no adelanta gas: sin bolsa deja de reajustar.":
    "The robot doesn't front gas: with no budget it stops rebalancing.",

  // --- Auditar.tsx ---
  "Tu vault": "Your vault",
  "el contrato que custodia tu dinero. Es tuyo: solo tu wallet puede sacar de él.":
    "the contract that holds your money. It's yours: only your wallet can withdraw from it.",
  "Dueño": "Owner",
  "Pool de {dex}": "{dex} pool",
  "el mercado donde trabaja tu dinero": "the market where your money works",
  "NFT de la posición": "Position NFT",
  "la posición de liquidez, a nombre del vault": "the liquidity position, in the vault's name",
  "Lógica (rebalanceador)": "Logic (rebalancer)",
  "hay una versión más nueva publicada — puedes adoptarla en Ajustes":
    "a newer version is published — you can adopt it in Settings",
  "es la última que publica tu factory": "it's the latest one your factory publishes",
  "Implementación": "Implementation",
  "el código que se clonó al crear tu vault": "the code that was cloned when your vault was created",
  "Robot (operador)": "Robot (operator)",
  "solo puede reajustar el rango. No puede mover tu dinero ni cambiar tus ajustes.":
    "it can only rebalance the range. It can't move your money or change your settings.",
  "Gestor de posiciones": "Position manager",
  "el NonfungiblePositionManager de Uniswap": "Uniswap's NonfungiblePositionManager",
  "Auditar esta piscina": "Audit this pool",
  "direcciones": "addresses",
  "Todo está en la cadena. Puedes comprobarlo tú sin fiarte de esta pantalla.":
    "Everything is on-chain. You can check it yourself without trusting this screen.",
  "Ver en el explorador": "View on the explorer",
  "Ver la pool en {sitio}": "View the pool on {sitio}",

  // --- Compartir.tsx ---
  "Tu aparato no dejó abrir el menú de compartir. Copia la imagen y pégala en el chat.":
    "Your device wouldn't open the share menu. Copy the image and paste it into the chat.",
  "Tu navegador no me deja copiar imágenes. Usa «Descargar» y adjúntala a mano.":
    "Your browser won't let me copy images. Use «Download» and attach it manually.",
  "Compartir tu resultado": "Share your result",
  "Cerrar": "Close",
  "Descargar": "Download",
  "Compartir con la imagen": "Share with the image",
  "Copiada ✓": "Copied ✓",
  "1 · Copiar imagen": "1 · Copy image",
  "2 · WhatsApp": "2 · WhatsApp",
  "2 · Telegram": "2 · Telegram",
  "tarjeta con tu resultado": "card with your result",
  "Dibujando…": "Drawing…",
  "Se abrirá el menú de tu móvil con ": "Your phone's menu will open with ",
  "la tarjeta ya adjunta": "the card already attached",
  ": elige WhatsApp, Telegram o lo que uses.": ": choose WhatsApp, Telegram or whatever you use.",
  "Copia la imagen primero": "Copy the image first",
  " y pégala en el chat cuando se abra: desde el escritorio, WhatsApp y Telegram solo aceptan el texto por enlace, la imagen hay que pegarla.":
    " and paste it into the chat when it opens: from desktop, WhatsApp and Telegram only accept the text via link, the image has to be pasted.",
  "La tarjeta ": "The card ",
  "no lleva tu importe, ni la dirección del vault, ni tu wallet":
    "doesn't carry your amount, or the vault's address, or your wallet",
  ". Solo el par, el porcentaje y el tiempo. Se dibuja en tu navegador: no se sube a ningún sitio hasta que tú la mandes.":
    ". Just the pair, the percentage and the time. It's drawn in your browser: it isn't uploaded anywhere until you send it.",

  // --- notas con interpolación (Resumen) ---
  "si el precio llega a este borde — incluye {monto} {simbolo} de reserva sin inyectar, que están fuera de la posición":
    "if the price reaches this edge — includes {monto} {simbolo} of reserve not yet injected, which is outside the position",

  // --- pie del robot bajo la banda (RobotReajustando): qué hace o por qué espera ---
  "El robot revisará tu piscina en breve": "The robot will check your pool shortly",
  "y moverá el rango a donde tu liquidez vuelva a cobrar si hace falta.":
    "and will move the range to where your liquidity earns again if needed.",
  "El precio acaba de salir del rango": "The price just left the range",
  "el robot lo verá en su próxima revisión y moverá tu rango si sigue fuera.":
    "the robot will see it on its next check and move your range if it's still outside.",
  "Reajustando ahora…": "Rebalancing now…",
  "el robot está enviando la transacción que mueve tu rango a donde tu liquidez vuelve a cobrar.":
    "the robot is sending the transaction that moves your range to where your liquidity earns again.",
  "Rango reajustado": "Range rebalanced",
  "el robot acaba de mover tu rango; la ficha se pondrá al día en unos segundos.":
    "the robot just moved your range; this page will catch up in a few seconds.",
  "Cerrando por objetivo de ganancia…": "Closing at profit target…",
  "tu piscina alcanzó el objetivo que marcaste y el robot la está liquidando.":
    "your pool reached the target you set and the robot is closing it.",
  "El robot espera a que el precio se aleje del borde (protege tu capital)":
    "The robot is waiting for the price to move away from the edge (protects your capital)",
  "reajustar pegado al borde convertiría en definitiva una pérdida que todavía se puede recuperar.":
    "rebalancing right at the edge would lock in a loss that can still be recovered.",
  "Esperando que el precio medio confirme la salida": "Waiting for the average price to confirm the exit",
  "el robot no reajusta por un pico momentáneo: espera a que el precio medio (TWAP) también salga del rango.":
    "the robot doesn't rebalance on a momentary spike: it waits for the average price (TWAP) to leave the range too.",
  "Esperando un precio seguro para reajustar": "Waiting for a safe price to rebalance",
  "el precio está justo en un punto en el que reajustar perdería la recuperación; se reintenta en unos minutos.":
    "the price sits exactly on a point where rebalancing would lose the recovery; it retries in a few minutes.",
  "Pausa entre reajustes": "Pause between rebalances",
  "tu piscina se reajustó hace poco y el robot respeta el tiempo mínimo entre reajustes que fijaste.":
    "your pool was rebalanced recently and the robot respects the minimum time between rebalances you set.",
  "Bolsa de gas sin saldo: recárgala para que el robot pueda reajustar":
    "Gas budget empty: top it up so the robot can rebalance",
  "el robot no adelanta gas que la bolsa no puede devolver. Recárgala desde la ficha (Bolsa de gas).":
    "the robot doesn't front gas the budget can't pay back. Top it up from this page (Gas budget).",
  "El robot está esperando a que baje el gas…": "The robot is waiting for gas to drop…",
  "reajustar con el gas caro puede costar diez veces más, y tu rango no cambia por esperar unos minutos.":
    "rebalancing with expensive gas can cost ten times more, and your range doesn't change by waiting a few minutes.",
  "la red está a {g} gwei y reajusta por debajo de {u}. Lleva {m} min esperando; si no baja, reajusta igual en {r} min.":
    "the network is at {g} gwei and it rebalances below {u}. It's been waiting {m} min; if it doesn't drop, it rebalances anyway in {r} min.",
  "Reajustar con el gas caro puede costar diez veces más, y tu rango no cambia por esperar unos minutos.":
    "Rebalancing with expensive gas can cost ten times more, and your range doesn't change by waiting a few minutes.",
  "Sin reajustes disponibles": "No rebalances left",
  "la piscina agotó su cuota de reajustes; amplíala en Ajustes para que el robot pueda moverla.":
    "the pool used up its rebalance quota; raise it in Settings so the robot can move it.",
  "Piscina en pausa": "Pool paused",
  "el robot no la toca mientras esté pausada.": "the robot doesn't touch it while it's paused.",
  "El robot de Keellabs no gestiona esta piscina": "The Keellabs robot doesn't manage this pool",
  "tiene asignado otro operador.": "it has a different operator assigned.",
  "El robot lo reintentará en la próxima revisión": "The robot will retry on its next check",
  "en el último intento el precio se movió o la simulación no pasó, así que no envió nada.":
    "on the last attempt the price moved or the simulation failed, so it sent nothing.",
  "desde hace menos de un minuto": "for less than a minute",
  "desde hace {m} min": "for {m} min",
  "próxima revisión del robot en ~{p} min": "next robot check in ~{p} min",
  "próxima revisión del robot en breve": "next robot check shortly",
  // --- Capital pendiente de colocar (CapitalPendiente.tsx) ---
  "Capital pendiente de colocar":
    "Capital waiting to be deployed",
  "de tu capital":
    "of your capital",
  "en la próxima revisión del robot (cada {r} min)":
    "on the robot's next check (every {r} min)",
  "en breve, en esta misma revisión":
    "shortly, in this same check",
  "en ~{m} min":
    "in ~{m} min",
  "Colocándose ahora":
    "Deploying now",
  "El robot está equilibrando los dos tokens y metiéndolos en tu rango. En unos segundos aparece dentro de la posición.":
    "The robot is balancing both tokens and adding them to your range. It will show inside the position in a few seconds.",
  "Colocándose por tramos":
    "Deploying in steps",
  "La pool es fina y meterlo de golpe movería el precio en tu contra. El robot coloca un tramo en cada revisión (cada {r} min) hasta terminar; el siguiente {c}.":
    "The pool is thin and adding it all at once would move the price against you. The robot adds one step per check (every {r} min) until done; the next one {c}.",
  "Ya colocado":
    "Already deployed",
  "El robot acaba de meterlo en la posición; la cifra se actualiza en unos segundos.":
    "The robot just added it to the position; the figure updates in a few seconds.",
  "Reintentando":
    "Retrying",
  "El último intento no pudo colocarlo (precio o simulación). Lo vuelve a intentar a las {h} ({c}).":
    "The last attempt couldn't deploy it (price or simulation). It retries at {h} ({c}).",
  "El último intento no pudo colocarlo (precio o simulación). Lo vuelve a intentar {c}.":
    "The last attempt couldn't deploy it (price or simulation). It retries {c}.",
  "Esperando gas":
    "Waiting for gas",
  "La bolsa de gas no alcanza para el barrido, y el robot no adelanta gas. Recarga la bolsa y entra en la siguiente revisión.":
    "The gas budget doesn't cover the sweep, and the robot doesn't front gas. Top up the budget and it goes in on the next check.",
  "Se coloca en el próximo reajuste":
    "Deployed on the next rebalance",
  "Es menos de {m} {s}: barrerlo ahora costaría más gas de lo que se coloca. Queda en el vault (sigue siendo tuyo) y entra solo en el próximo reajuste del rango.":
    "It's less than {m} {s}: sweeping it now would cost more gas than it deploys. It stays in the vault (still yours) and goes in automatically on the next range rebalance.",
  "Tu posición está fuera de rango y ahora solo admite uno de los dos tokens. Cuando el robot re-centre el rango, este capital entra junto con el resto.":
    "Your position is out of range and only accepts one of the two tokens right now. When the robot re-centers the range, this capital goes in with the rest.",
  "En esta piscina el robot solo puede meterlo re-centrando el rango, y no compensa hacerlo solo por esta cantidad. Entra en el próximo reajuste.":
    "In this pool the robot can only add it by re-centering the range, which isn't worth it for this amount alone. It goes in on the next rebalance.",
  "En cola para entrar":
    "Queued to deploy",
  "El robot lo detecta y lo mete en tu rango {c}. Equilibra los dos tokens con un swap y lo añade a la posición, sin mover el rango.":
    "The robot picks it up and adds it to your range {c}. It balances both tokens with a swap and adds them to the position, without moving the range.",
  "Al añadir capital o reinvertir ganancias el dinero entra primero al vault y luego el robot lo pasa a la posición. Mientras tanto no cobra comisiones, pero sigue siendo tuyo y sale entero si retiras.":
    "When you add capital or reinvest earnings, the money first lands in the vault and then the robot moves it into the position. Meanwhile it doesn't earn fees, but it's still yours and comes out in full if you withdraw.",
};
